/**
 * Product offers carousel: scroll-snap, dots, mouse drag, trackpad scroll, copy code.
 */
class ProductOffersSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  #activeIndex = 0;

  #isDragging = false;

  #pointerId = null;

  #startX = 0;

  #scrollLeft = 0;

  #dragDistance = 0;

  #scrollRaf = 0;

  #abort = new AbortController();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#track = this.querySelector('[data-offers-track]');
    if (!(this.#track instanceof HTMLElement)) return;

    this.#slides = Array.from(this.querySelectorAll('[data-offers-slide]'));
    this.#dots = Array.from(this.querySelectorAll('[data-offers-dot]'));

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.#track.addEventListener('scrollend', this.#onScrollEnd, { passive: true, signal });

    this.#dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const index = Number.parseInt(dot.dataset.index ?? '0', 10);
          this.#goToSlide(index);
        },
        { signal }
      );
    });

    this.#track.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#track.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.#track.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.#track.addEventListener('pointercancel', this.#onPointerUp, { signal });
    this.#track.addEventListener('lostpointercapture', this.#onPointerUp, { signal });

    this.#track.addEventListener('wheel', this.#onWheel, { passive: false, signal });

    this.querySelectorAll('[data-copy-offer-code]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.addEventListener('click', (event) => this.#onCopyClick(event, button), { signal });
      button.addEventListener('pointerdown', (event) => event.stopPropagation(), { signal });
    });

    this.#syncActiveFromScroll();
  }

  disconnectedCallback() {
    this.#abort.abort();
    cancelAnimationFrame(this.#scrollRaf);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      if (!this.#isDragging) {
        this.#syncActiveFromScroll();
      }
    });
  };

  #onScrollEnd = () => {
    if (!this.#isDragging) {
      this.#syncActiveFromScroll();
    }
  };

  #syncActiveFromScroll() {
    if (!this.#track || this.#slides.length === 0) return;

    const scrollLeft = this.#track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== this.#activeIndex) {
      this.#activeIndex = closestIndex;
      this.#updateDots();
    }
  }

  #updateDots() {
    this.#dots.forEach((dot, index) => {
      const isActive = index === this.#activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    const slide = this.#slides[index];
    if (!this.#track || !(slide instanceof HTMLElement)) return;

    this.#activeIndex = index;
    this.#updateDots();
    this.#scrollToSlide(slide, 'smooth');
  }

  /**
   * @param {HTMLElement} slide
   * @param {ScrollBehavior} behavior
   */
  #scrollToSlide(slide, behavior) {
    if (!this.#track) return;
    this.#track.scrollTo({
      left: slide.offsetLeft,
      behavior,
    });
  }

  #snapToNearestSlide() {
    if (!this.#track || this.#slides.length === 0) return;

    this.#syncActiveFromScroll();
    const slide = this.#slides[this.#activeIndex];
    if (slide) {
      this.#scrollToSlide(slide, 'smooth');
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!(this.#track instanceof HTMLElement)) return;
    if (event.target instanceof Element && event.target.closest('[data-copy-offer-code]')) return;

    // Touch uses native overflow scroll + scroll-snap so vertical page scroll still works.
    if (event.pointerType !== 'mouse') return;
    if (event.button !== 0) return;

    event.preventDefault();

    this.#isDragging = true;
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#dragDistance = 0;
    this.#scrollLeft = this.#track.scrollLeft;
    this.#track.style.scrollSnapType = 'none';
    this.#track.style.scrollBehavior = 'auto';
    this.#track.classList.add('is-dragging');

    try {
      this.#track.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#pointerId || !(this.#track instanceof HTMLElement)) return;

    event.preventDefault();

    const walk = event.clientX - this.#startX;
    this.#dragDistance = Math.max(this.#dragDistance, Math.abs(walk));
    this.#track.scrollLeft = this.#scrollLeft - walk;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#isDragging || (this.#pointerId !== null && event.pointerId !== this.#pointerId)) return;
    if (!(this.#track instanceof HTMLElement)) return;

    const didDrag = this.#dragDistance > 5;

    this.#isDragging = false;
    this.#pointerId = null;
    this.#dragDistance = 0;
    this.#track.classList.remove('is-dragging');
    this.#track.style.scrollSnapType = '';
    this.#track.style.scrollBehavior = '';

    try {
      this.#track.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }

    if (didDrag) {
      this.#snapToNearestSlide();
    } else {
      this.#syncActiveFromScroll();
    }
  };

  /**
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (!(this.#track instanceof HTMLElement)) return;

    const { deltaX, deltaY } = event;

    // Let vertical wheel / touchpad gestures scroll the page.
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    if (Math.abs(deltaX) < 1) return;

    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    const atStart = this.#track.scrollLeft <= 0;
    const atEnd = this.#track.scrollLeft >= maxScroll - 1;

    if ((deltaX < 0 && atStart) || (deltaX > 0 && atEnd)) return;

    event.preventDefault();
    this.#track.scrollLeft += deltaX;
    this.#syncActiveFromScroll();

    window.clearTimeout(this.#wheelSnapTimeout);
    this.#wheelSnapTimeout = window.setTimeout(() => this.#snapToNearestSlide(), 150);
  };

  #wheelSnapTimeout = 0;

  /**
   * @param {Event} event
   * @param {HTMLButtonElement} button
   */
  #onCopyClick = (event, button) => {
    event.preventDefault();
    event.stopPropagation();

    const code = button.dataset.copyOfferCode?.trim();
    if (!code) return;

    this.#copyText(code)
      .then(() => {
        button.classList.add('is-copied');
        const label = button.querySelector('[data-copy-label]');
        if (label instanceof HTMLElement) {
          const original = label.textContent;
          label.textContent = button.dataset.copiedLabel || 'Copied';
          window.setTimeout(() => {
            button.classList.remove('is-copied');
            label.textContent = original;
          }, 2000);
        }
      })
      .catch(() => {
        /* clipboard blocked */
      });
  };

  /**
   * @param {string} text
   * @returns {Promise<void>}
   */
  async #copyText(text) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        /* fallback below */
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

if (!customElements.get('product-offers-slider')) {
  customElements.define('product-offers-slider', ProductOffersSlider);
}
